import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/Input';
import { isSalesRole, SALES_ROLE_LABELS, type SalesRole } from '@shared/types/teams';

/**
 * Métier (27/10/2026): the person's job in the sales team, saved on their own
 * member row. The next-step planner reads it so a suggestion fits the job.
 */
export const SalesRoleField = ({ userId, role, detail }: {
  userId: string;
  role: SalesRole | null;
  detail: string | null;
}) => {
  const [value, setValue] = useState<SalesRole | ''>(role ?? '');
  const [words, setWords] = useState(detail ?? '');
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => { setValue(role ?? ''); setWords(detail ?? ''); }, [role, detail]);

  const save = async (next: { sales_role: SalesRole | null; sales_role_detail: string | null }) => {
    setStatus(null);
    const { error } = await supabase.from('tenant_members').update(next).eq('user_id', userId);
    setStatus(error ? 'Le métier n’a pas été enregistré.' : 'Enregistré');
  };

  return (
    <div className="mt-4 max-w-sm space-y-2">
      <label htmlFor="sales-role" className="block text-xs font-medium text-gray-600">Métier</label>
      <select
        id="sales-role"
        value={value}
        onChange={(event) => {
          const next = isSalesRole(event.target.value) ? event.target.value : '';
          setValue(next);
          void save({ sales_role: next || null, sales_role_detail: words.trim() || null });
        }}
        className="w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink"
      >
        <option value="">Non renseigné</option>
        {(Object.keys(SALES_ROLE_LABELS) as SalesRole[]).map((key) => (
          <option key={key} value={key}>{SALES_ROLE_LABELS[key]}</option>
        ))}
      </select>
      {value && (
        <Input
          id="sales-role-detail"
          value={words}
          maxLength={120}
          onChange={(event) => setWords(event.target.value)}
          onBlur={() => {
            if (words.trim() !== (detail ?? '')) void save({ sales_role: value || null, sales_role_detail: words.trim() || null });
          }}
          placeholder={value === 'other' ? 'Votre métier' : 'Précision, ex. « grands comptes industrie »'}
        />
      )}
      {status && <p className="text-xs text-domino-inkSoft" aria-live="polite">{status}</p>}
    </div>
  );
};
